import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Code, CodeDiffEditor, CodeEvent } from "@omegagrid/code";
import { msg } from '@omegagrid/localize';
import { AppComponent } from "../component";
import { CodeEditorAdapter, CodeEditorOptions, CodeHistoryAdapter } from "./codeEditorAdapter";
import { Toolbar } from "@omegagrid/toolbar";
import { actions, log } from "@omegagrid/core";
import { style } from "./codeEditor.style";
import { CodeLinkTarget, createCompletionRequest, createDocumentWordItems, findSqlFoldingRanges, toMonacoCompletions } from "@reporting/rp-utils-sql-completion";
import { TreeDropdown, TreeEvent } from "@omegagrid/tree";
import { HistoryItem, Saveable, ToolbarItemOptions } from "../types";
import { guard } from 'lit/directives/guard.js';
import rp from "../global";
import type { SettingsChangeEvent } from "../container/settings";
import { createMonacoOptions, EditorSettings, getEditorSettings, isEditorSetting } from "./editorSettings";
import "./editorSettingsMenu";

/** Number of lines a loaded chunk of content occupies (a trailing line break does not start a new line). */
const countLines = (content: string) => {
	if (!content) return 0;
	const breaks = content.split('\n').length - 1;
	return content.endsWith('\n') ? breaks : breaks + 1;
}

/** Monaco command run by Ctrl+click on a link; its argument is the key of the target in linkTargets */
const LINK_COMMAND = 'rp.code.openLink';
/** targets of the links currently offered by all editors, keyed per editor and link (see CodeEditor.registerLinks()) */
const linkTargets = new Map<string, CodeLinkTarget>();
let linkCommand: { dispose(): void } = null;
let linkEditorCount = 0;

/** Monaco instances the SQL folding is registered with */
const sqlFolding = new WeakSet<object>();

/**
 * Registers the folding of SQL (BEGIN ... END, CASE ... END, #region ...) with Monaco once. Unlike the completion,
 * it does not depend on the adapter: it answers for every SQL model, the diff editors included, and stays registered.
 */
const registerSqlFolding = (monaco: Code['monaco']) => {
	if (sqlFolding.has(monaco)) return;
	sqlFolding.add(monaco);
	const kinds = { comment: monaco.languages.FoldingRangeKind.Comment, region: monaco.languages.FoldingRangeKind.Region };
	monaco.languages.registerFoldingRangeProvider('sql', {
		provideFoldingRanges: model => findSqlFoldingRanges(model.getValue(), model.getOptions().tabSize)
			.map(range => ({ start: range.start, end: range.end, kind: range.kind ? kinds[range.kind] : undefined })),
	});
}

@customElement('rp-editor-code')
export class CodeEditor extends AppComponent implements Saveable {

	static styles = [style];

	static defaultEditorOptions = {
		tabSize: 4,
		insertSpaces: false
	};

	@property({type: Object})
	adapter: CodeEditorAdapter<CodeEditor>;

	@property({type: Boolean})
	rightToggle = false;

	@property({type: Boolean})
	closeButton = false;

	@query('og-code')
	editor: Code;

	@query('og-code-diff')
	diffEditor: CodeDiffEditor;

	@query('og-toolbar')
	toolbar: Toolbar;

	@query('#ddHistory')
	historyDropdown: TreeDropdown;

	@state()
	toolbarItems: ToolbarItemOptions[] = [];

	@state()
	history = false;

	@state()
	currentLiveItem: HistoryItem;

	@state()
	currentHistoryItem: HistoryItem;

	@state()
	historyAdapter: CodeHistoryAdapter;

	/** Monaco options of the place of use; the user settings (minimap, word wrap, font size, suggestions) come on top of them */
	@property({type: Object})
	editorOptions: Record<string, unknown> = {
		...CodeEditor.defaultEditorOptions,
	};

	@state()
	diffIdz: number;

	private _editorSettings: EditorSettings = getEditorSettings();
	private _monacoOptions: { source: Record<string, unknown>, settings: EditorSettings, fontSize: number, result: Record<string, unknown> } = null;
	private _defaultFontSize: number = undefined;

	/**
	 * The options handed to Monaco: editorOptions with the user settings applied. The same object is returned until
	 * either changes, so og-code is not asked to update its options on every render.
	 */
	get monacoOptions(): Record<string, unknown> {
		const cache = this._monacoOptions;
		if (cache && cache.source === this.editorOptions && cache.settings === this._editorSettings && cache.fontSize === this._defaultFontSize) return cache.result;
		const result = { ...this.editorOptions, ...createMonacoOptions(this._editorSettings, !!rp.host?.compact, this._defaultFontSize) };
		this._monacoOptions = { source: this.editorOptions, settings: this._editorSettings, fontSize: this._defaultFontSize, result };
		return result;
	}

	/**
	 * An editor setting changed (gear menu): Monaco gets the new options and the providers are registered again.
	 * The og-code element sits behind a guard() in render(), so the options are handed to it directly.
	 */
	private _onSettingsChange = (e: SettingsChangeEvent) => {
		if (!isEditorSetting(e.detail.id)) return;
		this._editorSettings = getEditorSettings();
		const options = this.monacoOptions;
		if (this.editor) this.editor.options = options;
		if (this.diffEditor) this.diffEditor.options = options;
		if (this.isReady) this.registerProviders();
	};

	protected options: CodeEditorOptions;
	private _pendingSave = false;
	private _saving = false;
	private _loading = false;
	private _loaded = false;
	private _offset = 0;
	private _content: string;
	private _editorReady = false;
	private _shouldLoadDiff = false;
	private _shouldLoadContent = false;
	private _editing = false;
	private _completion: { dispose(): void } = null;
	private _completionSeq = 0;
	private _links: { dispose(): void } = null;
	private _linksSeq = 0;
	private _linkKeys = new Set<string>();
	private readonly _linkEditorKey = `e${++linkEditorCount}`;
	private _linkSeq = 0;

	get isReady() { return this._editorReady }

	async load() {
		if (this._loading) return;
		this._loading = true;

		if (this._content) {
			this.editor.language = this.options.language;
			this.editor.content = this._content ?? '';
			this._loaded = true;
		} else {
			const uri = await this.adapter.getUri();
			const payload = await this.adapter.getContent({
				offset: this._offset,
				limit: this.options.pageSize
			});

			await this.editor.updateComplete;
			this.editor.uri = uri;
			this.editor.language = this.options.language;
			if (payload !== null) {
				const content = this.editor.content ?? '';
				const separator = content && !content.endsWith('\n') ? '\n' : '';
				this.editor.content = content + separator + (payload ?? '');
				this._offset += countLines(payload);
			}
			if (!this.options.pageSize || !payload) this._loaded = true;
		}

		this._loading = false;
		this._shouldLoadContent = false;
	}

	async loadDiff() {
		if (this._loading) return;
		this._loading = true;
		const content = await this.adapter.getHistoryContent(this.diffIdz);
		this.diffEditor.language = this.options.language;
		this.diffEditor.originalContent = this._content ?? '';
		this.diffEditor.modifiedContent = content ?? '';
		this._loading = false;
		this._shouldLoadDiff = false;
	}

	async updated(props: Map<PropertyKey, unknown>) {
		if (props.has('adapter') && this.adapter) {
			this.options = await this.adapter.getOptions();
			this.toolbarItems = await this.createToolbarItems();
			this.history = !!this.options.history;

			if (this.history) {
				this.historyAdapter = new CodeHistoryAdapter(this.adapter);
				this.currentLiveItem = await this.adapter.getCurrentItem();
			}

			this.adapter.attach(this);
			if (!this.diffIdz) this._shouldLoadContent = true;
		}

		if (props.has('diffIdz')) {
			this._shouldLoadDiff = !!this.diffIdz;
			this._shouldLoadContent = this._shouldLoadContent || !this._shouldLoadDiff;
		}

		if (this.isReady) {
			if (this._shouldLoadDiff) this.loadDiff();
			else if (this._shouldLoadContent) this.load();
		}
	}

	async createToolbarItems() {
		const options = this.options;
		if (!options.toolbar) return [];
		const items = Object.values<ToolbarItemOptions>(options.toolbar).filter(i => i.enabled !== false);
		items.sort((a, b) => (options.toolbar[a.id]?.ord ?? 0) - (options.toolbar[b.id]?.ord ?? 0));
		return items;
	}

	async save() {
		if (this._saving) {
			this._pendingSave = true;
			return;
		}

		this._saving = true;
		const result = await this.adapter.save(this.editor.editor.getValue());
		this._saving = false;

		if (this._pendingSave) {
			this._pendingSave = false;
			this.save();
		}

		if (result !== false) {
			this.historyAdapter?.invalidateItems();
			this._editing = false;
			this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		}
	}

	async discard(force: boolean = false) {
		if(!this._editing && !force) return;
		const content = await this.adapter.getContent();
		// not rendered (yet) - nothing to reset, the pending load pulls fresh content
		if (!this.editor) return;
		this.editor.content = content;
		this._offset = countLines(content);
		this._loaded = !this.options?.pageSize || !content;
		this._editing = false;
		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
	}

	async refresh() {
		if (this._editing) {
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: msg('Discard changes and refresh?'),
			});
			if (command?.id == 'Y') {
				this.discard();
			} else {
				return;
			}
		}
		this.discard(true);
	}

	async _onToolbarAction (e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'save': this.save(); break;
			case 'discard': this.discard(); break;
			case 'refresh': this.refresh(); break;
		}

		this.adapter?.onAction(e.action);
	}

	async ready() {
		if (this._shouldLoadContent) await this.load();
		else if (this._shouldLoadDiff) await this.loadDiff();

		this.editor.editor.updateOptions({readOnly: !!this.options?.readOnly});
		this._defaultFontSize = this.editor.editor.getOption(this.editor.monaco.editor.EditorOption.fontSize);
		this.editor.editor.updateOptions(this.monacoOptions);
		registerSqlFolding(this.editor.monaco);
		this.registerProviders();
		this.editor.editor.onDidChangeModelLanguage(() => this.registerProviders());
		this.editor.editor.onDidChangeModel(() => this.registerProviders());

		// keyboard shortcut using monaco editor onKeyDown
		this.editor.editor.onKeyDown((e) => {
			if (e.ctrlKey && e.code === 'KeyS') {
				e.preventDefault();
				e.stopPropagation();
				this.save();
			}
		});

		this.editor.editor.onDidScrollChange(e => {
			if (e.scrollTopChanged && !this._loaded && this.options?.pageSize > 0) {
				const h = this.editor.clientHeight;
				if (e.scrollTop + 2 * h >= e.scrollHeight) {
					this.load();
				}
			}
		});
	}

	/**
	 * Registers the adapter's completion provider with Monaco. Monaco providers are global per language, so the
	 * registered one answers only for the model of this editor and is disposed together with it.
	 */
	async registerCompletion() {
		const seq = ++this._completionSeq;
		// completion switched off by the user: neither the adapter's items nor Monaco's own (see createMonacoOptions)
		const provider = this._editorSettings.completion ? await this.adapter?.getCompletionProvider() : null;
		if (seq !== this._completionSeq) return;
		this.unregisterCompletion();
		const model = this.editor?.model;
		if (!provider || !model || !this.isConnected) return;

		const monaco = this.editor.monaco;
		const enums = {
			kinds: monaco.languages.CompletionItemKind as unknown as Record<string, number>,
			insertAsSnippet: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
		};
		this._completion = monaco.languages.registerCompletionItemProvider(model.getLanguageId(), {
			triggerCharacters: provider.triggerCharacters,
			provideCompletionItems: async (target, position, context, token) => {
				if (target !== this.editor?.model) return null;
				const { context: request, range } = createCompletionRequest(target, position, context.triggerCharacter);
				const items = [...(await provider.provide(request) ?? [])];
				if (token.isCancellationRequested) return null;
				if (provider.documentWords !== false && this._editorSettings.documentWords) {
					items.push(...createDocumentWordItems(target.getValue(), request.word, items.map(item => item.label)));
				}
				return { suggestions: toMonacoCompletions(items, request.wordPrefix, range, enums) };
			},
		});
	}

	unregisterCompletion() {
		this._completion?.dispose();
		this._completion = null;
	}

	/**
	 * Registers the adapter's link provider with Monaco: the ranges get underlined and Ctrl+click runs the
	 * target's open() through a Monaco command. The registration is global per language like the completion one, so the
	 * provider answers only for the model of this editor; the targets live in a module registry the command
	 * looks into, keyed per editor and link.
	 */
	async registerLinks() {
		const seq = ++this._linksSeq;
		const provider = await this.adapter?.getLinkProvider();
		if (seq !== this._linksSeq) return;
		this.unregisterLinks();
		const model = this.editor?.model;
		if (!provider || !model || !this.isConnected) return;

		const monaco = this.editor.monaco;
		if (!linkCommand) {
			linkCommand = monaco.editor.registerCommand(LINK_COMMAND, (_accessor: unknown, key: string) => {
				Promise.resolve(linkTargets.get(key)?.open()).catch((error: unknown) => log.getLogger('rp').warn(`Code link failed: ${error}`));
			});
		}
		this._links = monaco.languages.registerLinkProvider(model.getLanguageId(), {
			provideLinks: async (target, token) => {
				if (target !== this.editor?.model) return null;
				const targets = await provider.provide(target.getValue()) ?? [];
				if (token.isCancellationRequested) return null;
				const keys = targets.map(link => {
					const key = `${this._linkEditorKey}-${this._linkSeq++}`;
					linkTargets.set(key, link);
					this._linkKeys.add(key);
					return key;
				});
				return {
					links: targets.map((link, i) => ({
						range: link.range,
						tooltip: link.tooltip,
						url: `command:${LINK_COMMAND}?${encodeURIComponent(JSON.stringify([keys[i]]))}`,
					})),
					// Monaco disposes the list when it recomputes the links or the model goes away
					dispose: () => keys.forEach(key => this.forgetLink(key)),
				};
			},
		});
	}

	unregisterLinks() {
		this._links?.dispose();
		this._links = null;
		this._linkKeys.forEach(key => linkTargets.delete(key));
		this._linkKeys.clear();
	}

	private forgetLink(key: string) {
		linkTargets.delete(key);
		this._linkKeys.delete(key);
	}

	/** Registers the adapter's Monaco providers; a failure is logged and the editor works on without that feature. */
	private registerProviders() {
		const warn = (what: string) => (error: unknown) => log.getLogger('rp').warn(`${what} registration failed: ${error}`);
		this.registerCompletion().catch(warn('Code completion'));
		this.registerLinks().catch(warn('Code links'));
	}

	connectedCallback() {
		super.connectedCallback();
		rp.settings.addEventListener('change', this._onSettingsChange);
		// re-attached (tab switch): the Monaco editor survived, the providers were disposed on detach
		if (this.isReady && !this._completion && !this._links) {
			this.registerProviders();
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		rp.settings.removeEventListener('change', this._onSettingsChange);
		this.unregisterCompletion();
		this.unregisterLinks();
	}

	edit() {
		this._editing = true;
	}

	_onChange = (e: CodeEvent) => {
		this.edit();
		this.dispatchEvent(new CustomEvent('edit', {detail: e, bubbles: true, composed: true}));
	}

	_onReady = () => {
		this._editorReady = true;
		this.ready();
	}

	_onDiffChange = () => {
		this._content = this.diffEditor?.modifiedContent;
	}

	_onHistoryChange = async (e: TreeEvent) => {
		const idz = e.node?.key;
		if (!this.diffIdz) {
			this._content = this.editor?.content;
		}
		this.diffIdz = idz ? parseInt(idz) : null;
	}

	get hasToolbar() {
		return this.history || this.rightToggle || (!this.options?.readOnly && this.toolbarItems?.length > 0);
	}

	render = () => html`
		${this.hasToolbar ? html`
			<div class="controls">
				<og-toolbar
					.items="${this.toolbarItems}"
					@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
				</og-toolbar>

				<div id="empty"></div>

				${this.history ? html`
					<div id="history">
						<og-tree-dropdown
							cross
							.placeholder="${this.currentLiveItem ? this.historyAdapter.formatItem(this.currentLiveItem) : ''}"
							id="ddHistory"
							color="gray"
							.adapter="${this.historyAdapter}"
							@change="${this._onHistoryChange}">
						</og-tree-dropdown>
					</div>
				` : ''}

				<rp-editor-settings-menu .language="${this.options?.language}"></rp-editor-settings-menu>

				${this.rightToggle ? html`
					<og-button
						icon="square-caret-left"
						style="width: 30px"
						@click="${() => this.dispatchEvent(new CustomEvent('toggleRight'))}"
						color="transparent">
					</og-button>
				` : ''}

				${this.closeButton ? html`
					<og-close @click="${() => this.dispatchEvent(new CustomEvent('close'))}"></og-close>
				` : ''}
			</div>
		` : ''}

		<div class="body">
			${guard([this.adapter, this.diffIdz], () => this.diffIdz ? html`
				<og-code-diff
					.positionOffset=${{t: 1, l: 3, r: -5, b: -1}}
					.language="${this.options?.language}"
					@code.change="${this._onDiffChange}"
					.options="${this.monacoOptions}">
				</og-code-diff>
			` : html`
				<og-code
					.positionOffset=${{t: 1, l: 3, r: -5, b: -1}}
					.language="${this.options?.language}"
					@code.ready="${this._onReady}"
					@code.change="${this._onChange}"
					.options="${this.monacoOptions}">
				</og-code>
			`)}
		</div>
	`;

	layout() {
		this.editor?.layout();
	}

}
